import { ArrowDownLeft, ArrowLeft, Check, CircleAlert, Clock3, Info, Lock, PlaneTakeoff, Plus, ShieldCheck, SlidersHorizontal, Sparkles, Undo2, X } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ButtonLabel } from "../../components/ButtonLabel";
import { KuspitNotice } from "../../components/Disclosures";
import { photoBackground } from "../../components/Cards";
import { Modal } from "../../components/Modal";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest, describeApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import { formatCalendarDate, formatTimestamp } from "../../lib/format";
import { useApartado, useLibreWallet, useMyRequest, useReservationConfig } from "../../lib/queries";
import { APARTADO_STATUS_LABELS, movementCopy, mxn, requestTimeline, reservationStatusCopy } from "../../lib/reservations";
import type { Apartado, ApartadoMovement, ApiEnvelope } from "../../types";
import { FundingForm } from "./ApartadoCreatePage";
import { InvestmentInfo } from "./InvestmentInfo";

const HERO_SHADE = "linear-gradient(155deg, rgba(0,37,87,0.75) 0%, rgba(0,49,212,0.55) 60%, rgba(0,20,45,0.85) 100%)";

/**
 * "Mi Apartado" (C4, design 6a step 4): the confirmed earmark, its goal and progress, the
 * reservation status kept separate from the balance, and the append-only activity. "Añadir
 * fondos" repeats the funding controls; "Ajustar mi meta" only raises the goal (C5.1).
 */
export function ApartadoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const apartado = useApartado(id);
  const request = useMyRequest(apartado.data?.requestId);
  const { wallet } = useLibreWallet();
  const config = useReservationConfig();
  const [toast, setToast] = useState<{ title: string; message: string; icon: "check" | "info" } | null>(null);
  const [fundOpen, setFundOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  usePageHeader("Mi Apartado", apartado.data ? apartado.data.tripName : undefined);

  useEffect(() => {
    const state = location.state as { created?: boolean; amount?: number } | null;
    if (state?.created) {
      setToast({ icon: "check", title: "Apartado creado correctamente", message: `Transferimos ${mxn(state.amount ?? 0)} desde tu Wallet Libre.` });
      window.history.replaceState({}, "");
    }
  }, [location.state]);
  // "Ajustar mi meta" from Solicitud recibida lands straight in the goal dialog.
  useEffect(() => {
    if (apartado.data && new URLSearchParams(location.search).get("ajustar") === "1" && apartado.data.status !== "closed") setGoalOpen(true);
  }, [apartado.data, location.search]);
  useEffect(() => { if (goalOpen && apartado.data) track("apartado_goal_change_started", { apartado_id: apartado.data.id, actor_type: "customer", direction: "increase" }); }, [goalOpen, apartado.data]);

  if (apartado.isLoading) return <div className="page-state"><Spinner label="Cargando Apartado" /><span>Cargando Apartado</span></div>;
  if (apartado.isError || !apartado.data) {
    return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No encontramos este Apartado</strong><button className="secondary-button" onClick={() => navigate("/wallet/libre")}>Ir a Wallet Libre</button></div>;
  }
  const data = apartado.data;
  const req = request.data?.request;
  const reservation = req ? reservationStatusCopy(req.status) : null;
  const progress = data.progress ?? 0;
  const free = wallet?.balance.free ?? data.walletFree ?? 0;
  const active = data.status === "active";
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ["apartados"] }), queryClient.invalidateQueries({ queryKey: ["wallets"] }), queryClient.invalidateQueries({ queryKey: ["reservations"] })]);

  return (
    <div className="page-container apartado-page">
      <button type="button" className="back-button" onClick={() => navigate("/wallet/libre")}><ArrowLeft size={16} aria-hidden="true" /> Wallet Libre</button>
      {toast && (
        <div className="inline-notice" role="status">
          {toast.icon === "check" ? <Check size={17} aria-hidden="true" /> : <Info size={17} aria-hidden="true" />}
          <span><strong>{toast.title}</strong> {toast.message}</span>
          <button type="button" className="icon-button ghost" onClick={() => setToast(null)} aria-label="Cerrar aviso"><X size={16} aria-hidden="true" /></button>
        </div>
      )}
      <div className="wallet-grid">
        <div className="wallet-column">
          <section className="wallet-hero apartado-hero" style={photoBackground(data.request?.tripPhotoUrl, HERO_SHADE, "wide")}>
            <div className="hero-top">
              <div className="hero-dream">
                <span className="hero-eyebrow">Apartado</span>
                <h2>Apartado para {data.tripName}</h2>
                <div className="hero-pills">
                  <span className={`hero-pill ${active ? "active" : data.status === "closed" ? "closed" : "pending"}`}><i aria-hidden="true" />{APARTADO_STATUS_LABELS[data.status]}</span>
                  <span className="glass-pill"><Lock size={12} aria-hidden="true" /> Sin retiros</span>
                </div>
              </div>
            </div>
            <div className="hero-bottom">
              <div>
                <span className="hero-label">{data.status === "closed" ? "Apartado cerrado · fondos devueltos a Wallet Libre" : "Apartado"}</span>
                <strong className="hero-amount" translate="no">{mxn(data.balance)}</strong>
                <div className="hero-yield"><Sparkles size={15} aria-hidden="true" /><span>Reservado para tu viaje</span></div>
                <div className="goal-progress" aria-label={`${Math.round(progress * 100)}% de tu meta`}>
                  <div className="goal-bar"><span style={{ width: `${Math.round(progress * 100)}%` }} /></div>
                  <span translate="no">{Math.round(progress * 100)}% de tu meta de {mxn(data.goalAmount)}{data.appliedAmount > 0 ? ` · ${mxn(data.appliedAmount)} ya aplicados al viaje` : ""} · faltan {mxn(data.remaining)}</span>
                </div>
              </div>
              <div className="hero-actions">
                <button type="button" className="hero-button solid" onClick={() => setFundOpen(true)} disabled={!active}><Plus size={16} aria-hidden="true" /><span>Añadir fondos</span></button>
                {data.request?.tripSlug && <button type="button" className="hero-button ghost" onClick={() => navigate(`/viajes/${data.request!.tripSlug}`)}><PlaneTakeoff size={16} aria-hidden="true" /><span>Ver mi viaje</span></button>}
                <button type="button" className="hero-button ghost" onClick={() => setInfoOpen(true)}><Info size={16} aria-hidden="true" /><span>Más info</span></button>
              </div>
            </div>
          </section>

          <section className="card goal-card">
            <div className="section-heading small" style={{ margin: 0 }}>
              <div><span className="page-eyebrow">Tu meta de viaje</span><strong className="goal-figure" translate="no">{mxn(data.goalAmount)}</strong></div>
              <button type="button" className="secondary-button small" onClick={() => setGoalOpen(true)} disabled={data.status === "closed"}><SlidersHorizontal size={14} aria-hidden="true" /> Ajustar mi meta</button>
            </div>
            <p>Esta meta contempla tu paquete actual. Puedes aumentarla para sumar vuelos, experiencias y otros servicios. Aumentar la meta no añade ni confirma servicios: tu asesor cotiza y confirma cada uno.</p>
          </section>

          <section className="movements-section">
            <div className="section-heading">
              <div><span className="page-eyebrow">Actividad</span><h2>Actividad del Apartado</h2></div>
              <span translate="no">{data.movements.length} movimientos</span>
            </div>
            {data.movements.length ? (
              <div className="movement-list">{data.movements.map((movement) => <MovementRow key={movement.id} movement={movement} />)}</div>
            ) : (
              <div className="empty-state"><Clock3 size={28} aria-hidden="true" /><strong>Sin movimientos todavía</strong><span>Haz tu primer abono y aparecerá aquí, junto con lo que se aplique a tu viaje.</span></div>
            )}
          </section>
          <KuspitNotice />
        </div>

        <div className="wallet-rail">
          <section className="card reservation-status">
            <span className="rail-title">Estado de la reservación</span>
            {req && reservation ? (
              <>
                <div className="label-row"><strong>{reservation.label}</strong><span className="request-meta" translate="no">{req.folio}</span></div>
                <p>{reservation.note}</p>
                <ol className="timeline compact">
                  {requestTimeline(req).map((step) => <li key={step.key} className={step.state}><span className="timeline-dot" aria-hidden="true" /><div><strong>{step.title}</strong><span>{step.detail}</span></div></li>)}
                </ol>
                {req.departureDate && <span className="request-meta" translate="no">Salida {formatCalendarDate(req.departureDate, "long")} · {req.travelers} viajeros</span>}
                <button type="button" className="link-button" onClick={() => navigate(`/solicitudes?sel=${req.id}`)}>Ver solicitud</button>
              </>
            ) : <p className="rail-empty">Cargando el estado de tu solicitud…</p>}
            <p className="resv-fine"><ShieldCheck size={13} aria-hidden="true" /> Crear o abonar tu Apartado no confirma la reservación: tu asesor te escribe en cuanto los servicios estén garantizados.</p>
          </section>
          <section className="card">
            <span className="rail-title">Wallet Libre</span>
            <p className="request-meta">Disponible para abonar: <strong translate="no">{mxn(free)}</strong></p>
            <button type="button" className="secondary-button small full" onClick={() => navigate("/wallet/libre")}>Ir a mi Wallet Libre</button>
          </section>
        </div>
      </div>

      <Modal open={fundOpen} onOpenChange={setFundOpen} label="Añadir fondos">
        <div className="dialog-content wide funding-dialog">
          <button type="button" className="dialog-close" onClick={() => setFundOpen(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
          {wallet && config.data ? (
            <FundingForm
              apartadoId={data.id} tripName={data.tripName} minimum={0} goal={data.goalAmount} walletId={wallet.id} free={free} currentBalance={data.balance}
              consentVersion={config.data.consentVersion} documentsVersion={config.data.documentsVersion} fundLabel={config.data.investment.fundLabel}
              onInfo={() => { setFundOpen(false); setInfoOpen(true); }} onCancel={() => setFundOpen(false)}
              onCreated={(updated: Apartado) => { setFundOpen(false); setToast({ icon: "check", title: "Transferencia realizada", message: `Tu Apartado para ${updated.tripName} ahora tiene ${mxn(updated.balance)}.` }); void refresh(); }}
            />
          ) : <div className="dialog-loading"><Spinner /> Cargando tu Wallet Libre…</div>}
        </div>
      </Modal>

      <GoalDialog apartado={data} open={goalOpen} onOpenChange={setGoalOpen} onSaved={(updated) => { setToast({ icon: "check", title: "Meta actualizada", message: `Tu nueva meta es ${mxn(updated.goalAmount)}. No se movió dinero ni se confirmaron servicios.` }); void refresh(); }} />

      <Modal open={infoOpen} onOpenChange={setInfoOpen} label="Conoce cómo se invierte tu dinero">
        <div className="dialog-content wide">
          <button type="button" className="dialog-close" onClick={() => setInfoOpen(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
          {config.data && <InvestmentInfo fundLabel={config.data.investment.fundLabel} infoText={config.data.investment.infoText} prospectusUrl={config.data.prospectusUrl} fundInfoUrl={config.data.fundInfoUrl} />}
        </div>
      </Modal>
    </div>
  );
}

function MovementRow({ movement }: { movement: ApartadoMovement }) {
  const copy = movementCopy(movement);
  const Icon = copy.tone === "in" ? ArrowDownLeft : copy.tone === "back" ? Undo2 : copy.tone === "out" ? ShieldCheck : Sparkles;
  const reversed = movement.status === "reversed";
  return (
    <article className={`movement-row${reversed ? " reversed" : ""}`}>
      <div className={`movement-icon ${copy.tone}`}><Icon size={19} aria-hidden="true" /></div>
      <div className="movement-copy"><strong>{copy.title}</strong><span>{formatTimestamp(movement.createdAt)} · {copy.sub}</span></div>
      <div className={`movement-status ${reversed ? "failed" : movement.status === "pending" ? "pending" : "settled"}`}>
        {reversed ? <Undo2 size={16} aria-label="Revertido" /> : movement.status === "pending" ? <Clock3 size={16} aria-label="En proceso" /> : <Check size={16} aria-label="Confirmado" />}
        <span>{reversed ? "Revertido" : movement.status === "pending" ? "En proceso" : "Confirmado"}</span>
      </div>
      <div className={`movement-amount ${movement.amount < 0 ? "negative" : "positive"}`} translate="no">
        {movement.kind === "goal_change"
          ? <strong>{mxn(movement.newGoal ?? 0)}</strong>
          : <strong>{movement.amount > 0 ? "+" : ""}{mxn(movement.amount)}</strong>}
        <span>{movement.kind === "goal_change" ? "nueva meta" : `saldo ${mxn(movement.balanceAfter)}`}</span>
      </div>
    </article>
  );
}

/** "Ajustar mi meta": only upwards, with the resulting progress shown before confirming (C5.1). */
function GoalDialog({ apartado, open, onOpenChange, onSaved }: { apartado: Apartado; open: boolean; onOpenChange(open: boolean): void; onSaved(apartado: Apartado): void }) {
  const [amount, setAmount] = useState(String(Math.round(apartado.goalAmount)));
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  useEffect(() => { if (open) { setAmount(String(Math.round(apartado.goalAmount))); setReason(""); setError(null); } }, [open, apartado.goalAmount]);
  const proposed = Number(amount.replace(/[^\d.]/g, "")) || 0;
  const valid = proposed > apartado.goalAmount;
  const projected = proposed > 0 ? Math.min(1, (apartado.balance + apartado.appliedAmount) / proposed) : 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) { setError([`Solo puedes aumentar tu meta. El mínimo permitido es ${mxn(apartado.goalAmount)}.`]); return; }
    setSubmitting(true); setError(null);
    try {
      const payload = await apiRequest<ApiEnvelope<{ apartado: Apartado }>>(`/api/v1/apartados/${apartado.id}/goal`, { method: "PATCH", body: JSON.stringify({ goalAmount: proposed, reason: reason.trim() || undefined }) });
      onOpenChange(false);
      onSaved(payload.data.apartado);
    } catch (cause) { setError(describeApiError(cause, "No pudimos actualizar tu meta.")); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} label="Ajustar mi meta">
      <div className="dialog-content">
        <button type="button" className="dialog-close" onClick={() => onOpenChange(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
        <span className="page-eyebrow">Mi Apartado</span>
        <h2>Ajustar mi meta</h2>
        <p>Súmale a tu meta los servicios adicionales que quieras para tu viaje. Solo puedes aumentarla; no se mueve dinero ni se confirman servicios.</p>
        <form className="dialog-form" onSubmit={submit}>
          <div className="after-grid">
            <div><small>Meta actual</small><strong translate="no">{mxn(apartado.goalAmount)}</strong></div>
            <div><small>Meta propuesta</small><strong translate="no">{mxn(proposed)}</strong></div>
          </div>
          <div className="field-block">
            <label htmlFor="goal-new">Nueva meta (MXN)</label>
            <input id="goal-new" className="standalone-input large" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value.replace(/[^\d.]/g, ""))} />
            <span className="field-hint">Mínimo permitido: <span translate="no">{mxn(Math.max(apartado.goalAmount, apartado.minimumGoal))}</span>.</span>
          </div>
          <div className="goal-progress" aria-label={`${Math.round(projected * 100)}% de la meta propuesta`}>
            <div className="goal-bar"><span style={{ width: `${Math.round(projected * 100)}%` }} /></div>
            <span translate="no">Con la meta propuesta llevarías el {Math.round(projected * 100)}%.</span>
          </div>
          <div className="field-block">
            <label htmlFor="goal-reason">¿Qué quieres sumar? <span className="label-optional">· opcional</span></label>
            <textarea id="goal-reason" className="standalone-input textarea" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} maxLength={600} placeholder="Ej. vuelos internacionales, una noche extra en Delhi, seguro de viaje" />
            <span className="field-hint">Tu asesor lo verá para entender el cambio. Aumentar la meta no añade ni confirma servicios.</span>
          </div>
          {error && <div className="form-error" role="alert">{error.join(" ")}</div>}
          <button type="submit" className="primary-button solid full" disabled={!valid || submitting}><ButtonLabel busy={submitting} busyLabel="Guardando" label="Confirmar nueva meta" /></button>
        </form>
      </div>
    </Modal>
  );
}
